import {
	CircleNotificationsOutlined,
	ExpandMore,
	OpenInNew,
} from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Badge,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	deleteSystemNotifications,
	ESRThunkDispatch,
} from "../../actions/form";
import { SysNotificationT } from "../../types/data";

dayjs.extend(relativeTime);

function colorForImportance(importance: number) {
	switch (importance) {
		case 2:
			return "#F72A2A";
		case 1:
			return "#18CE41";
		default:
			return "#C7C7C7";
	}
}

type Props = {
	rid: string;
	notifications: SysNotificationT[];
};
const formatShortName = (shortName: string) => {
	return shortName.replaceAll("_", " ");
};
export function SystemNotificationRow({ rid, notifications }: Props) {
	const dispatch = useDispatch<ESRThunkDispatch>();
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedNotificationId, setSelectedNotificationId] = useState<
		number | string | null
	>(null);
	return (
		<>
			<Accordion
				key={rid}
				expanded={expanded}
				onChange={(_, willBeExpanded) => {
					setExpanded(willBeExpanded);
				}}
			>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Stack
						direction="row"
						sx={{ width: "100%", alignItems: "center", gap: "16px" }}
					>
						{notifications?.length > 0 && (
							<>
								<Box sx={{ width: 20, flex: "none" }}>
									<Badge
										color="error"
										badgeContent={
											notifications?.filter((notif) => notif.status === 0)
												.length
										}
									>
										<CircleNotificationsOutlined />
									</Badge>
								</Box>
								{notifications.map((notif, idx) => (
									<Typography
										component="div"
										key={idx}
										sx={{
											width: 500,
											fontWeight: "bold",
											display: "flex",
											alignItems: "center",
										}}
									>
										<span>{formatShortName(notif.short_name)}</span>
										<Box sx={{ flex: "none" }} />
										<IconButton
											sx={{ flex: "none" }}
											onClick={(e) => {
												e.stopPropagation();
												if (notif.link_type.startsWith("org:")) {
													navigate("/organizations", {
														state: { searchString: notif.link_type.replace("org:", "") },
													});
												} else {
													navigate("/usersmanagement", {
														state: { searchString: notif.email },
													});
												}
											}}
										>
											<OpenInNew />
										</IconButton>
									</Typography>
								))}
							</>
						)}
					</Stack>
				</AccordionSummary>
				<AccordionDetails>
					{notifications?.map((notif) => {
						return (
							<Fragment key={notif.id}>
								<Divider sx={{ flex: 1, my: "8px" }} />
								<Stack
									direction="row"
									sx={{
										overflow: "hidden",
										alignItems: "center",
										my: "8px",
										gap: "16px",
									}}
								>
									<Typography sx={{ fontSize: 12, flex: "none" }}>
										{dayjs(notif.timestamp).format("MM-DD-YYYY")}
									</Typography>
									<Divider orientation="vertical" sx={{ height: "20px" }} />
									<Box
										sx={{
											flex: "none",
											width: 6,
											height: 6,
											borderRadius: 3,
											background: colorForImportance(notif.importance),
										}}
									/>
									<Typography
										fontWeight={notif.status > 0 ? "normal" : "bold"}
										fontSize={16}
										my="8px"
										sx={{ cursor: "default" }}
									>
										System Message: {notif.display_name}
									</Typography>

									<Divider orientation="vertical" sx={{ height: "20px" }} />
									{notif.deadline && (
										<Typography
											style={{
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
												fontSize: 12,
												color:
													dayjs(notif.timestamp).diff(dayjs()) < 0
														? "red"
														: "black",
											}}
										>
											Due {dayjs(notif.deadline).fromNow()}
										</Typography>
									)}
									{!notif.short_name.startsWith("User Signup") &&
										!notif.short_name.startsWith("User Account Activation") && (
											<>
												<Button
													color="error"
													size="small"
													variant="outlined"
													onClick={(e) => {
														e.stopPropagation();
														setSelectedNotificationId(notif.id);
														setDeleteDialogOpen(true);
														// dispatch delete action here
														// dispatch(deleteSystemNotifications(notif.id));
													}}
												>
													Delete
												</Button>
											</>
										)}
								</Stack>
							</Fragment>
						);
					})}
				</AccordionDetails>
			</Accordion>
			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
			>
				<DialogTitle>Delete Notification</DialogTitle>

				<DialogContent>
					<DialogContentText>
						This notification will be deleted for all Admins. Are you sure?
					</DialogContentText>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={() => {
							setDeleteDialogOpen(false);
						}}
					>
						No
					</Button>

					<Button
						color="error"
						variant="contained"
						onClick={() => {
							if (selectedNotificationId !== null) {
								dispatch(deleteSystemNotifications(selectedNotificationId));
							}

							setDeleteDialogOpen(false);
							setSelectedNotificationId(null);
						}}
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
